import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TimePicker, Popover, Input as ShopeeInput } from 'shopee-ui-react';
import { debounce } from 'lodash';
import styles from './index.module.scss';

interface Time {
  hours: number;
  minutes: number;
  seconds: number;
}

interface TimeSelectProps {
  value?: string;
  onChange?: (value: string) => void;
  formatTime?: 'HH:mm' | 'HH:mm:ss';
}

const TimeSelect: React.FC<TimeSelectProps> = ({
  value,
  onChange,
  formatTime = 'HH:mm',
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [time, setTime] = useState<Time>({ hours: 0, minutes: 0, seconds: 0 });
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [lastValidTime, setLastValidTime] = useState<Time | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const timePickerRef = useRef<any>(null);
  const inputRef = useRef<any>(null);
  const currentUserAction = useRef<'input' | 'timePicker' | 'clear'>('input');
  const debouncedHandleInputChange = useRef(
    debounce((value: string) => {
      const timeParts = value.split(':');
      if (
        timeParts.length === 2 ||
        (formatTime === 'HH:mm:ss' && timeParts.length === 3)
      ) {
        const [hours, minutes, seconds] = timeParts.map(part =>
          parseInt(part, 10)
        );
        if (
          !isNaN(hours) &&
          !isNaN(minutes) &&
          (seconds === undefined || !isNaN(seconds))
        ) {
          if (
            hours >= 0 &&
            hours <= 23 &&
            minutes >= 0 &&
            minutes <= 59 &&
            (seconds === undefined || (seconds >= 0 && seconds <= 59))
          ) {
            const newTime = { hours, minutes, seconds: seconds || 0 };
            setTime(newTime);
            setLastValidTime(newTime);
          }
        }
      }
    }, 350)
  ).current;

  useEffect(() => {
    if (value !== undefined) {
      setInputValue(value);
      const timeParts = value.split(':');
      if (
        (timeParts.length === 2 ||
          (formatTime === 'HH:mm:ss' && timeParts.length === 3)) &&
        currentUserAction.current === 'timePicker'
      ) {
        const [hours, minutes, seconds] = timeParts.map(part =>
          parseInt(part, 10)
        );
        if (
          !isNaN(hours) &&
          !isNaN(minutes) &&
          (seconds === undefined || !isNaN(seconds))
        ) {
          setTime({ hours, minutes, seconds: seconds || 0 });
          setLastValidTime({ hours, minutes, seconds: seconds || 0 });
        }
      }
    }
  }, [value, formatTime]);

  const formatTimeValue = useCallback(
    (time: Time) => {
      const pad = (num: number) => num.toString().padStart(2, '0');
      if (formatTime === 'HH:mm:ss') {
        return `${pad(time.hours)}:${pad(time.minutes)}:${pad(time.seconds)}`;
      } else {
        return `${pad(time.hours)}:${pad(time.minutes)}`;
      }
    },
    [formatTime]
  );

  const handleInputChange = (value: string) => {
    currentUserAction.current = 'input';
    setInputValue(value);
    setPopoverVisible(true);
    debouncedHandleInputChange(value);
  };

  const handleInputClear = () => {
    setLastValidTime(null);
    setPopoverVisible(false);
    setInputValue('');
    setErrorMessage('');
    currentUserAction.current = 'clear';
    if (onChange) onChange('');
  };

  const handleInputFocus = () => {
    setPopoverVisible(true);
    setInputValue(formatTimeValue(time));
  };

  const handleInputBlur = () => {
    setPopoverVisible(false);
    if (currentUserAction.current === 'clear') {
      return;
    }
    const timeParts = inputValue.split(':');
    if (timeParts.length === 2 || timeParts.length === 3) {
      const [hours, minutes, seconds] = timeParts.map(part =>
        parseInt(part, 10)
      );
      if (
        !isNaN(hours) &&
        !isNaN(minutes) &&
        (seconds === undefined || !isNaN(seconds))
      ) {
        if (
          hours >= 0 &&
          hours <= 23 &&
          minutes >= 0 &&
          minutes <= 59 &&
          (seconds === undefined || (seconds >= 0 && seconds <= 59))
        ) {
          const newTime = { hours, minutes, seconds: seconds || 0 };
          setTime(newTime);
          setLastValidTime(newTime);
          setInputValue(formatTimeValue(newTime));
          if (onChange) onChange(formatTimeValue(newTime));
          return;
        }
      }
    }
    if (lastValidTime) {
      setInputValue(formatTimeValue(lastValidTime));
    } else {
      setInputValue('');
    }
  };

  const handleTimeChange = (date: Date | null, timeString: string) => {
    if (date) {
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const seconds = date.getSeconds();
      const newTime = { hours, minutes, seconds };
      setLastValidTime(newTime);
      if (currentUserAction.current === 'timePicker') {
        setInputValue(formatTimeValue(newTime));
      }
    } else {
      setTime({ hours: 0, minutes: 0, seconds: 0 });
      setInputValue('');
      if (onChange) onChange('');
    }
  };

  const handlePopoverVisibleChange = (visible: boolean) => {
    setPopoverVisible(visible);
  };

  return (
    <div>
      <Popover
        className={styles.time_select_popover}
        trigger="click"
        showArrow={false}
        placement="bottomLeft"
        content={
          <div
            onMouseDown={e => {
              currentUserAction.current = 'timePicker';
              e.preventDefault();
            }}
            onMouseEnter={e => {
              currentUserAction.current = 'timePicker';
              e.preventDefault();
            }}>
            <TimePicker
              ref={timePickerRef}
              value={time ? formatTimeValue(time) : undefined}
              onChange={handleTimeChange}
              showConfirm={false}
              withoutPopover={true}
              format={formatTime}
            />
          </div>
        }
        visible={popoverVisible}>
        <ShopeeInput
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onClear={e => {
            e.preventDefault();
            handleInputClear();
          }}
          clearable
          placeholder={'hh:mm'}
        />
        {errorMessage && <div style={{ color: 'red' }}>{errorMessage}</div>}
      </Popover>
    </div>
  );
};

export default TimeSelect;
